/**
 * WebDateInput — sélecteur de date web 100% lang-aware (3 dropdowns).
 *
 * v1.12.268 — Remplace l'<input type="date"> HTML5 (qui utilisait la langue
 * du navigateur/OS, impossible à override sur Safari iOS) par 3 selects
 * Day / Month / Year avec mois traduits en EN/FR selon la langue active.
 *
 * Fonctionnement :
 *  - Sur native (iOS/Android) : retourne null, le caller gère son DateTimePicker
 *  - Sur web : 3 dropdowns côte-à-côte, fully lang-aware
 *
 * Output ISO string 'YYYY-MM-DD' identique à l'ancien composant pour rétrocompat.
 */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Platform, View } from 'react-native';
import { getCurrentLang } from '../i18n';

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function pad2(n) { return String(n).padStart(2, '0'); }

function parseISO(iso) {
  if (!iso || typeof iso !== 'string') return { y: '', m: '', d: '' };
  const parts = iso.split('-');
  if (parts.length !== 3) return { y: '', m: '', d: '' };
  return { y: parts[0], m: parts[1], d: parts[2] };
}

function buildISO(y, m, d) {
  if (!y || !m || !d) return '';
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

export default function WebDateInput({
  value,           // 'YYYY-MM-DD' ou ''
  onChange,        // (iso: string) => void
  min,             // 'YYYY-MM-DD'
  max,             // 'YYYY-MM-DD'
  hasError = false,
  style,
  inputStyle,
  lang: langProp,
}) {
  if (Platform.OS !== 'web') return null;

  // Détection langue
  let lang = langProp;
  if (!lang) { try { lang = getCurrentLang() || 'fr'; } catch { lang = 'fr'; } }
  const months = lang === 'en' ? MONTHS_EN : MONTHS_FR;
  const dayLabel   = lang === 'en' ? 'Day'   : 'Jour';
  const monthLabel = lang === 'en' ? 'Month' : 'Mois';
  const yearLabel  = lang === 'en' ? 'Year'  : 'Année';

  // v1.12.360 — état interne pour préserver les sélections partielles.
  //   Avant : si l'user ne sélectionnait QUE le jour, buildISO renvoyait ''
  //   et le parent reset birthDate='' → la sélection était perdue au re-render.
  //   Désormais : on garde { d, m, y } en local, et on n'émet onChange QUE
  //   quand les 3 sont remplis (string ISO valide).
  const parsed = parseISO(value);
  const [localD, setLocalD] = useState(parsed.d);
  const [localM, setLocalM] = useState(parsed.m);
  const [localY, setLocalY] = useState(parsed.y);
  // Sync depuis prop value (ex: chargement initial async depuis DB)
  const lastValueRef = useRef(value);
  useEffect(() => {
    if (lastValueRef.current === value) return;
    lastValueRef.current = value;
    const p = parseISO(value);
    setLocalD(p.d);
    setLocalM(p.m);
    setLocalY(p.y);
  }, [value]);

  const d = localD;
  const m = localM;
  const y = localY;

  // Bornes années depuis min/max
  const minDate = parseISO(min);
  const maxDate = parseISO(max);
  const minY = parseInt(minDate.y || '1900', 10);
  const maxY = parseInt(maxDate.y || String(new Date().getFullYear()), 10);
  const years = useMemo(() => {
    const arr = [];
    for (let i = maxY; i >= minY; i--) arr.push(i);
    return arr;
  }, [minY, maxY]);

  // Jours selon mois/année (gestion fév 29 année bissextile, etc.)
  const days = useMemo(() => {
    const yy = parseInt(y || maxY, 10);
    const mm = parseInt(m || '1', 10);
    if (!yy || !mm) return Array.from({ length: 31 }, (_, i) => i + 1);
    const lastDay = new Date(yy, mm, 0).getDate();
    return Array.from({ length: lastDay }, (_, i) => i + 1);
  }, [y, m, maxY]);

  // Helper : émet onChange UNIQUEMENT quand d+m+y sont tous remplis
  const emitIfComplete = (nd, nm, ny) => {
    if (nd && nm && ny) {
      const iso = buildISO(ny, nm, nd);
      lastValueRef.current = iso;
      onChange?.(iso);
    }
  };
  const onChangeY = (newY) => { setLocalY(newY); emitIfComplete(d, m, newY); };
  const onChangeM = (newM) => { setLocalM(newM); emitIfComplete(d, newM, y); };
  const onChangeD = (newD) => { setLocalD(newD); emitIfComplete(newD, m, y); };

  const baseStyle = {
    height: 46,
    borderRadius: 12,
    border: `1px solid ${hasError ? '#C62828' : '#E8E4DC'}`,
    backgroundColor: '#FFFFFF',
    paddingLeft: 10,
    paddingRight: 8,
    fontSize: 14,
    fontFamily: 'inherit',
    color: '#1A1814',
    boxSizing: 'border-box',
    outline: 'none',
    cursor: 'pointer',
    ...(inputStyle || {}),
  };

  return (
    <View style={[{ flexDirection: 'row', gap: 8 }, style]}>
      {/* Day */}
      {React.createElement('select', {
        value: d || '',
        onChange: (e) => onChangeD(e.target.value),
        style: { ...baseStyle, flex: 1 },
        'aria-label': dayLabel,
      },
        React.createElement('option', { value: '', disabled: true }, dayLabel),
        ...days.map(day => React.createElement('option', { key: day, value: pad2(day) }, day))
      )}
      {/* Month */}
      {React.createElement('select', {
        value: m || '',
        onChange: (e) => onChangeM(e.target.value),
        style: { ...baseStyle, flex: 1.6 },
        'aria-label': monthLabel,
      },
        React.createElement('option', { value: '', disabled: true }, monthLabel),
        ...months.map((name, i) => React.createElement('option', { key: i, value: pad2(i + 1) }, name))
      )}
      {/* Year */}
      {React.createElement('select', {
        value: y || '',
        onChange: (e) => onChangeY(e.target.value),
        style: { ...baseStyle, flex: 1.2 },
        'aria-label': yearLabel,
      },
        React.createElement('option', { value: '', disabled: true }, yearLabel),
        ...years.map(yr => React.createElement('option', { key: yr, value: String(yr) }, yr))
      )}
    </View>
  );
}
